import React, { useContext } from 'react';
import { Button, Tooltip, Divider, Flex, Typography } from 'antd';
import { ThemeContext } from '../../../../../pages/_app';
import {
  EventIcon,
  ConditionIcon,
  ActionIcon,
  TargetIcon,
  EventLinkIcon,
  QuestIcon,
  CollectibleIcon,
  DestroyableIcon,
} from '@src/components/scene/canvas-html/editorIcons';
import { NODES_TYPES, AVAILABLE_FOR_CONNECT_NODES } from '../types';
import styles from './timeline-action-buttons.module.css';

interface TimelineActionButtonsProps {
  addNodeAtCenter: (type: NODES_TYPES) => void;
  selectedNode: any | null;
}

const { Text } = Typography;

const utilitiesNodesButtons = [
  {
    type: NODES_TYPES.TARGET,
    label: 'Target',
    icon: <TargetIcon />,
    tooltip: 'Add Target',
  },
  {
    type: NODES_TYPES.EVENT_LINK,
    label: 'Link',
    icon: <EventLinkIcon />,
    tooltip: 'Add Link',
  },
];

const mainNodesButtons = [
  {
    type: NODES_TYPES.EVENT,
    label: 'Event',
    icon: <EventIcon />,
    tooltip: 'Add Event',
  },
  {
    type: NODES_TYPES.QUEST,
    label: 'Quest',
    icon: <QuestIcon />,
    tooltip: 'Add Quest',
  },
];

const functionsNodesButtons = [
  {
    type: NODES_TYPES.CONDITION,
    label: 'Condition',
    icon: <ConditionIcon />,
    tooltip: 'Add Condition',
  },
  {
    type: NODES_TYPES.ACTION,
    label: 'Action',
    icon: <ActionIcon />,
    tooltip: 'Add Action',
  },
  {
    type: NODES_TYPES.COLLECTIBLE,
    label: 'Collectible',
    icon: <CollectibleIcon />,
    tooltip: 'Add Collectible',
  },
  {
    type: NODES_TYPES.DESTROYABLE,
    label: 'Destroyable',
    icon: <DestroyableIcon />,
    tooltip: 'Add Destroyable',
  },
];

const TimelineActionButtons: React.FC<TimelineActionButtonsProps> = ({
  addNodeAtCenter,
  selectedNode,
}) => {
  const { isDark } = useContext(ThemeContext);
  const selectedNodeType = selectedNode?.type as NODES_TYPES | undefined;
  const availableForConnectNodes = selectedNodeType
    ? AVAILABLE_FOR_CONNECT_NODES[selectedNodeType]
    : [];

  function applyDisabledStyleButton(buttonType: NODES_TYPES): string {
    if (
      !selectedNodeType ||
      availableForConnectNodes.includes(buttonType) ||
      !availableForConnectNodes.length
    ) {
      return '';
    }

    return styles.disabledButton;
  }

  return (
    <div className={`${styles.buttonsContainer} ${isDark ? styles.dark : ''}`}>
      <div>
        <Text type='secondary' className={styles.smallText}>
          Main Nodes
        </Text>
        <Flex align='center' gap={8}>
          {mainNodesButtons.map((button) => (
            <Tooltip
              key={button.type}
              title={button.tooltip}
              placement='bottom'
            >
              <Button
                type='default'
                className={`${styles.actionButton} ${applyDisabledStyleButton(
                  button.type
                )}`}
                onClick={() => addNodeAtCenter(button.type)}
                icon={button.icon}
              >
                {button.label}
              </Button>
            </Tooltip>
          ))}
          <Divider type='vertical' className={styles.buttonsTimelineDivider} />
        </Flex>
      </div>
      <div>
        <Text type='secondary' className={styles.smallText}>
          Function Nodes
        </Text>
        <Flex align='center' gap={8}>
          {functionsNodesButtons.map((button) => (
            <Tooltip
              key={button.type}
              title={button.tooltip}
              placement='bottom'
            >
              <Button
                type='default'
                className={`${styles.actionButton} ${applyDisabledStyleButton(
                  button.type
                )}`}
                onClick={() => addNodeAtCenter(button.type)}
                icon={button.icon}
              >
                {button.label}
              </Button>
            </Tooltip>
          ))}
          <Divider type='vertical' className={styles.buttonsTimelineDivider} />
        </Flex>
      </div>
      <div>
        <Text type='secondary' className={styles.smallText}>
          Utility Nodes
        </Text>
        <Flex align='center' gap={8}>
          {utilitiesNodesButtons.map((button) => (
            <Tooltip
              key={button.type}
              title={button.tooltip}
              placement='bottom'
            >
              <Button
                type='default'
                className={`${styles.actionButton} ${applyDisabledStyleButton(
                  button.type
                )}`}
                onClick={() => addNodeAtCenter(button.type)}
                icon={button.icon}
              >
                {button.label}
              </Button>
            </Tooltip>
          ))}
        </Flex>
      </div>
    </div>
  );
};

export default TimelineActionButtons;
